import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { LuPlus, LuDices } from "react-icons/lu";
import { FiSearch, FiSliders, FiDownload } from "react-icons/fi";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";
import ListPageSearch from "../list-page/ListPageSearch";
import ListPageEmpty from "../list-page/ListPageEmpty";
import ListPager from "../list-page/ListPager";

import GameTable from "./GameTable";
import GameDetails from "./GameDetails";
import GameCancelModal from "./GameCancelModal";
import GameFilters from "./GameFilters";
import ExportXLS from "../ExportXLS";

import { useGetGamesQuery } from "../../store/services/gameServices";
import { gameDetailsActions } from "../../store/gameDetailsSlice";
import { gameCancelModalActions } from "../../store/gameCancelModal";
import { gamesFiltersModalActions } from "../../store/gamesFiltersModalSlice";
import { exportXLSModalActions } from "../../store/exportXLSModalSlice";
import { hasMinimumRole, ROLES } from "../../utils/roleUtils";

const PAGE_LIMIT = 10;

export default function GameRulesTable() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isOpen: isGameDetailsOpen } = useSelector((s) => s.game_details);
  const { isOpen: isGameFiltersModalOpen } = useSelector((s) => s.game_filters_modal);
  const { isOpen: isGameCancelModalOpen } = useSelector((s) => s.game_cancel_modal);
  const isExportXLSOpen = useSelector((s) => s.export_xls_modal.isOpen);

  const [filteredGames, setFilteredGames] = useState([]);
  const [pages, setPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [params, setParams] = useState({});
  const [searchTextInput, setSearchTextInput] = useState("");
  const [query] = useState(new URLSearchParams(window.location.search));

  const { data, isLoading, isFetching } = useGetGamesQuery({ params });

  /* Hydrate from URL */
  useEffect(() => {
    const newParams = {};
    query.forEach((v, k) => (newParams[k] = v));
    setParams(newParams);
    if (query.has("page")) setCurrentPage(parseInt(query.get("page")));
    if (query.has("q")) setSearchTextInput(query.get("q"));
  }, [query]);

  /* Sync data */
  useEffect(() => {
    if (data) {
      setFilteredGames(data?.data?.data || []);
      setPages({
        currentPage: data?.data?.currentPage,
        totalPages: data?.data?.totalPages,
        totalCount: data?.data?.totalCount,
      });
    }
  }, [data]);

  /* Reflect params into URL */
  useEffect(() => {
    const filtered = Object.entries(params || {})
      .filter(([_, v]) => v != null)
      .reduce((acc, [k, v]) => {
        acc[k] = v;
        return acc;
      }, {});
    const urlParams = new URLSearchParams(filtered).toString();
    navigate(`/games?${urlParams}`);
  }, [params, navigate]);

  /* Search debounce */
  const debounceRef = useRef(null);
  const handleSearchTextInputChange = (e) => {
    const text = e.target.value;
    setSearchTextInput(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length > 2) {
      debounceRef.current = setTimeout(() => {
        setParams((p) => ({ ...p, q: text, page: 1 }));
        setCurrentPage(1);
      }, 400);
    } else {
      setParams((p) => {
        const { q, ...rest } = p;
        return rest;
      });
    }
  };

  const handleSort = (value) => {
    setParams((p) => {
      if (p.sort_by === value && p.sort_order === "asc") {
        const { sort_by, sort_order, ...rest } = p;
        return rest;
      }
      return {
        ...p,
        sort_by: value,
        sort_order: p.sort_by === value && p.sort_order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setPages((p) => ({ ...p, currentPage: page }));
    setParams((p) => ({ ...p, page }));
  };

  const handleRowClick = (game) => {
    dispatch(gameDetailsActions.openModal(game));
  };

  const handleCancel = (game) => {
    dispatch(gameCancelModalActions.openModal(game));
  };

  const handleApplyAdvancedParams = (next) => {
    setParams(next);
  };

  const openExport = () => {
    dispatch(
      exportXLSModalActions.openModal({
        params: { category: "games", ...params },
      })
    );
  };

  const openFilters = () => dispatch(gamesFiltersModalActions.openModal());

  const advancedFilterKeys = ["status", "type", "start_date", "end_date", "municipalities_no", "activities", "fiscal_parity"];
  const hasAdvancedFilters = advancedFilterKeys.some((k) => params[k]);
  const hasFilters = !!(params.q || hasAdvancedFilters);

  const handleClearFilters = () => {
    setSearchTextInput("");
    setCurrentPage(1);
    setParams({});
  };

  const isEmpty = !isLoading && !isFetching && filteredGames.length === 0;

  const totalCount = pages?.totalCount ?? 0;
  const rangeLabel = useMemo(() => {
    if (!totalCount) return "";
    const start = (currentPage - 1) * PAGE_LIMIT + 1;
    const end = Math.min(currentPage * PAGE_LIMIT, totalCount);
    return `Po shfaqen ${start}–${end} prej ${totalCount.toLocaleString()}`;
  }, [currentPage, totalCount]);

  return (
    <>
      {isGameDetailsOpen && <GameDetails />}
      {isExportXLSOpen && <ExportXLS />}
      {isGameCancelModalOpen && <GameCancelModal />}
      {isGameFiltersModalOpen && (
        <GameFilters params={params} handleApplyAdvancedParams={handleApplyAdvancedParams} />
      )}

      <ListPageShell>
        <ListPageHeader
          eyebrow={{ icon: <LuDices size={18} />, label: "Lojërat" }}
          title="Menaxho lojërat"
          subtitle={
            totalCount
              ? `${totalCount.toLocaleString()} lojëra në sistem`
              : "Lista e lojërave të krijuara"
          }
          actions={
            hasMinimumRole(ROLES.OFFICIAL_1) && (
              <button
                type="button"
                onClick={() => navigate("/games/add")}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 active:scale-[0.98] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition"
              >
                <LuPlus size={18} />
                Krijo lojë
              </button>
            )
          }
        />

        <ListPageSearch
          value={searchTextInput}
          onChange={handleSearchTextInputChange}
          placeholder="Kërko sipas titullit..."
          hasFilters={hasFilters}
          onClearFilters={handleClearFilters}
          extras={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={openFilters}
                className={`h-11 inline-flex items-center justify-center gap-1.5 rounded-xl border px-4 text-sm font-semibold shadow-sm transition ${
                  hasAdvancedFilters
                    ? "border-[var(--secondary-color)] bg-[var(--secondary-color)]/10 text-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/15"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FiSliders size={14} />
                Filtrat
                {hasAdvancedFilters && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold bg-[var(--secondary-color)] text-white">
                    {advancedFilterKeys.filter((k) => params[k]).length}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={openExport}
                className="h-11 inline-flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 px-4 text-sm font-semibold text-gray-700 shadow-sm transition"
              >
                <FiDownload size={14} />
                Eksporto
              </button>
            </div>
          }
        />

        {isEmpty ? (
          <ListPageEmpty
            icon={hasFilters ? FiSearch : LuDices}
            title={hasFilters ? "Asnjë lojë nuk u gjet" : "Ende asnjë lojë"}
            subtitle={
              hasFilters
                ? "Provoni të ndryshoni ose të pastroni filtrat për të parë më shumë rezultate."
                : "Lojërat e krijuara do të shfaqen këtu."
            }
            action={
              hasFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition"
                >
                  Pastro filtrat
                </button>
              )
            }
          />
        ) : (
          <GameTable
            games={filteredGames}
            isLoading={isLoading || isFetching}
            params={params}
            onSort={handleSort}
            onRowClick={handleRowClick}
            onCancel={handleCancel}
          />
        )}

        {!isEmpty && (
          <ListPager
            currentPage={currentPage}
            totalPages={pages?.totalPages || 1}
            onChange={handlePageChange}
            rangeLabel={rangeLabel}
          />
        )}
      </ListPageShell>
    </>
  );
}
